import Component from '@glimmer/component';

import { on } from '@ember/modifier';

import { classMix } from 'rise-ui/decorators/class-mix.js';
import call from 'rise-ui/helpers/call.js';
import classMod from 'rise-ui/helpers/class-mod.js';
import fnx from 'rise-ui/helpers/fnx.js';
import not from 'rise-ui/helpers/not.js';
import or from 'rise-ui/helpers/or.js';

/**
 * CheckAllButtonComponent input attributes
 * @typedef CheckAllButtonComponentArgs
 * @property {{}} [checkByDefault] True to check, false to uncheck; if some but not all are checked (default: false)
 * @property {boolean} [isAllChecked] Whether all list items are checked
 * @property {boolean} [isNoneChecked] Whether no list items are checked
 * @property {Function} [action] Action called on button click
 */
/**
 * CheckAllButtonComponent template properties
 * @typedef CheckAllButtonComponentProperties
 * @property {boolean} check Whether clicking should check all list items
 * @property {Function} [action] Action called on button click
 */
/**
 * Facilitates check/uncheck or of a list
 * @typedef CheckAllButtonComponent
 * @type {CheckAllButtonComponent & CheckAllButtonComponentArgs & CheckAllButtonComponentProperties & ClassMix}
 */
export default class CheckAllButtonComponent extends Component {
  /** @type {ClassMix} */
  @classMix classMixed;

  defaultCheckByDefault = false;
  get checkByDefault() {
    const { defaultCheckByDefault, args: { checkByDefault } } = this;
    return checkByDefault ?? defaultCheckByDefault;
  }
  get check() {
    const { checkByDefault, args: { isAllChecked, isNoneChecked } } = this;
    return isAllChecked ? false
      : isNoneChecked ? true
      : checkByDefault;
  }

  <template>
    <button type="button"
      class={{classMod
        (call this.classMixed.mixClassNames "c-check-all-button")
        this.check "--check-all"
        (not this.check) "--uncheck-all"
        modifiers=(call this.classMixed.mixClassModifiers)
      }}
      title={{if this.check
        (or @checkTitle @checkText "Check All")
        (or @uncheckTitle @uncheckText "Uncheck All")
      }}
      {{on "click" (fnx @action this.check)}}>
      {{~if this.check
        (or @checkButtonText @checkText "Check All")
        (or @uncheckButtonText @uncheckText "Uncheck All")
      ~}}
    </button>
  </template>
}
